import { MidnightVerificationWindowConfig } from "./types";

function parseConfigDate(dateStr: string): Date | null {
    const [day, month, year] = dateStr.split("/").map(Number);
    if (!day || !month || !year) {
        return null;
    }

    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** Parses times like "9:00 PM" / "7:00 AM" into minutes from midnight. */
function parseAmPmTimeToMinutes(timeStr: string): number | null {
    const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) {
        return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const period = match[3].toUpperCase();

    if (
        !Number.isFinite(hours) ||
        !Number.isFinite(minutes) ||
        hours < 1 ||
        hours > 12 ||
        minutes < 0 ||
        minutes > 59
    ) {
        return null;
    }

    if (hours === 12) {
        hours = 0;
    }
    if (period === "PM") {
        hours += 12;
    }

    return hours * 60 + minutes;
}

function isDateInFeatureWindow(date: Date, config: MidnightVerificationWindowConfig): boolean {
    const startDate = parseConfigDate(config.from_date || "");
    const endDate = parseConfigDate(config.to_date || "");

    if (!startDate || !endDate) {
        return false;
    }

    const dayOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dayOnly >= startDate && dayOnly <= endDate;
}

function isTimeInPaymentWindow(date: Date, config: MidnightVerificationWindowConfig): boolean {
    const startMinutes = parseAmPmTimeToMinutes(config.payment_start_time || "");
    const endMinutes = parseAmPmTimeToMinutes(config.payment_end_time || "");

    if (startMinutes == null || endMinutes == null) {
        return false;
    }

    const nowMinutes = date.getHours() * 60 + date.getMinutes();

    // Same-day window (e.g. 9:00 AM → 5:00 PM)
    if (startMinutes <= endMinutes) {
        return nowMinutes >= startMinutes && nowMinutes < endMinutes;
    }

    // Overnight window (e.g. 9:00 PM → 7:00 AM)
    return nowMinutes >= startMinutes || nowMinutes < endMinutes;
}

/** Returns true when verification payment is required for posting at `date` (local time). */
export function isMidnightPostingVerificationWindow(
    date: Date,
    config?: MidnightVerificationWindowConfig | null
): boolean {
    if (!config?.is_enabled) {
        return false;
    }

    return isDateInFeatureWindow(date, config) && isTimeInPaymentWindow(date, config);
}
